import {
    Transform,
    Point,
    clampTransform,
    canPan,
    computePanBoundaries,
} from '../../src-js/category-description/ipl-math';

export interface ZoomControllerConfig {
    container: HTMLElement;
    content: HTMLElement;
    minZoom?: number;
    maxZoom?: number;
    zoomStep?: number;
    scrollMultiplier?: number;
    pinchSensitivity?: number;
    onTransformChange?: (transform: Transform) => void;
    showControls?: boolean;
    controlsPrefix?: string;
    /**
     * Element the zoom buttons are prepended to. Defaults to `container`, where the
     * buttons float over the drawing. Pass an ancestor of `container` to render them
     * as a toolbar before it instead (class `{prefix}-zoom-controls--toolbar`), so
     * they cannot cover any part of the drawing.
     */
    controlsHost?: HTMLElement;
    /** Play a one-time scale-pulse + button-highlight animation to hint zoom capability */
    showDiscoveryAnimation?: boolean;
}

type PointerGestureMode = 'idle' | 'pending' | 'pan-x' | 'pan-2d' | 'scroll-native';

/**
 * Mark an element placed INSIDE the zoom container that must keep its own taps
 * (e.g. an overlay with a button). The iOS touch fallback leaves one-finger touches
 * that start on it alone: no preventDefault, so iOS still fires the click.
 */
export const ZOOM_PASSTHROUGH_ATTR = 'data-zoom-passthrough';

interface ActivePointer {
    id: number;
    clientX: number;
    clientY: number;
}

export class ZoomController {
    private container: HTMLElement;
    private content: HTMLElement;
    private config: Required<ZoomControllerConfig>;
    private controlsEl: HTMLElement | null = null;

    // Transform state
    private transform: Transform = {
        scale: 1,
        translateX: 0,
        translateY: 0,
    };

    // Mouse drag state
    private isDragging = false;
    private dragStart: Point = { x: 0, y: 0 };
    private dragInitial: Point = { x: 0, y: 0 };

    // Pointer state
    private activePointers = new Map<number, ActivePointer>();
    private pointerMode: PointerGestureMode = 'idle';
    private readonly pointerSlop = 8;

    // One-finger anchor
    private oneFingerStart: Point = { x: 0, y: 0 };
    private oneFingerInitial: Point = { x: 0, y: 0 };

    // Two-finger state (pointer path)
    private lastPinchDistance: number | null = null;
    private lastPinchMidpoint: Point | null = null;

    // iOS touch fallback detection
    private readonly useTouchFallback: boolean;
    // iOS touch gesture state
    private lastTouchPoint: Point | null = null;
    private touchStartPoint: Point | null = null;
    private readonly touchSlop = 6;
    private iosOwnsGesture = false;
    // A one-finger touch that started on the zoom buttons or a passthrough element:
    // move/end ignore it too, so a stale 'pending' mode cannot cancel it mid-tap.
    private touchPassesThrough = false;

    // Two-finger state (touch fallback path)
    private lastTouchDistance: number | null = null;
    private lastTouchMidpoint: Point | null = null;

    // iOS tap synthesis. Owning a one-finger touch in a zoomed container means
    // preventDefault() on touchstart, and iOS then never fires the click — so a
    // tap on a part marker was dead at zoom > 1. A one-finger touch that starts
    // zoomed is a tap candidate until it moves past touchSlop, a second finger
    // joins, or it is cancelled; if it ends as a candidate we deliver the click
    // ourselves (see deliverTapClick).
    private tapCandidate: { target: EventTarget | null; startedAt: number } | null = null;
    // Past this a press reads as a hold (iOS's own long-press threshold), not a tap.
    private readonly tapMaxDurationMs = 500;
    // Belt and braces against a double click: should a platform fire the native
    // click after the cancelled touchstart after all, the capture-phase guard
    // swallows the next foreign click on the same target inside this window.
    private lastSyntheticClick: Event | null = null;
    private nativeClickSuppression: { target: EventTarget; until: number } | null = null;
    private readonly nativeClickSuppressionMs = 300;

    // Discovery animation state
    private hasShownDiscovery = false;
    private discoveryObserver: IntersectionObserver | null = null;

    // Window width tracking for resize detection
    private previousWindowWidth: number = window.innerWidth;

    // Raw viewport-space clientY of the last touch — used for page-scroll deltas.
    // Container-relative lastTouchPoint.y cannot be used because window.scrollTo()
    // moves the container, changing rect.top and zeroing out the delta.
    private lastTouchClientY: number = 0;

    // iOS does not update window.scrollY synchronously after scrollTo(), so reading
    // it each frame as the base for the next call causes every scroll to reset to the
    // same stale position. We track the intended target ourselves and accumulate.
    private targetScrollY: number = 0;

    // Event listeners storage for cleanup
    private listeners: Array<{
        element: HTMLElement | Window | Document;
        event: string;
        handler: EventListener;
        options?: AddEventListenerOptions | boolean;
    }> = [];

    constructor(config: ZoomControllerConfig) {
        this.container = config.container;
        this.content = config.content;

        this.config = {
            container: config.container,
            content: config.content,
            minZoom: config.minZoom ?? 1,
            maxZoom: config.maxZoom ?? 5,
            zoomStep: config.zoomStep ?? 0.1,
            scrollMultiplier: config.scrollMultiplier ?? 0.001,
            pinchSensitivity: config.pinchSensitivity ?? 0.01,
            onTransformChange: config.onTransformChange ?? (() => { }),
            showControls: config.showControls ?? true,
            controlsPrefix: config.controlsPrefix ?? 'datte',
            controlsHost: config.controlsHost ?? config.container,
            showDiscoveryAnimation: config.showDiscoveryAnimation ?? false,
        };

        this.useTouchFallback = this.shouldUseTouchFallback();
        this.init();
    }

    private init(): void {
        if (this.config.showControls) {
            this.injectZoomControls();
        }

        this.bindEvents();
        this.container.style.touchAction = this.computeTouchAction();

        if (this.config.showDiscoveryAnimation) {
            this.setupDiscoveryAnimation();
        }
    }

    private shouldUseTouchFallback(): boolean {
        const hasTouch =
            'ontouchstart' in window || navigator.maxTouchPoints > 0;

        if (!hasTouch) return false;

        const uaData = (navigator as Navigator & {
            userAgentData?: { platform?: string };
        }).userAgentData;

        const isIOS =
            uaData?.platform === 'iOS' ||
            /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        return !!isIOS;
    }
    /**
     * Gotcha: never put anything inside `container` that changes its box (a toolbar,
     * padding). The pan math clamps against the container's rect and assumes the
     * content's layout origin is the container's top-left corner, so extra height
     * leaves part of the drawing unreachable. An in-flow toolbar therefore goes to
     * `controlsHost`, outside the container.
     */
    private injectZoomControls(): void {
        const prefix = this.config.controlsPrefix;
        const controlsClass = `${prefix}-zoom-controls`;
        const host = this.config.controlsHost;

        // A controller re-created on the same drawing reuses the existing toolbar.
        const existing = Array.from(host.children).find((el) => el.classList.contains(controlsClass));
        if (existing) {
            this.controlsEl = existing as HTMLElement;
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = controlsClass;
        if (host !== this.container) {
            wrapper.classList.add(`${controlsClass}--toolbar`);
        }
        wrapper.setAttribute('role', 'group');
        wrapper.setAttribute('aria-label', 'Zoom');

        const buttons = [
            { cls: 'in', label: 'Vergrößern' },
            { cls: 'reset', label: 'Zoom zurücksetzen' },
            { cls: 'out', label: 'Verkleinern' },
        ];

        buttons.forEach((btn) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = `${prefix}-zoom-button ${prefix}-zoom-${btn.cls}`;
            button.setAttribute('aria-label', btn.label);
            button.setAttribute('title', btn.label);
            wrapper.appendChild(button);
        });

        host.prepend(wrapper);
        this.controlsEl = wrapper;
    }

    private bindEvents(): void {
        const prefix = this.config.controlsPrefix;

        if (this.controlsEl) {
            this.addListener(this.controlsEl, 'click', ((e: Event) => {
                const target = e.target as HTMLElement;
                if (target.classList.contains(`${prefix}-zoom-in`)) {
                    this.zoomIn();
                } else if (target.classList.contains(`${prefix}-zoom-out`)) {
                    this.zoomOut();
                } else if (target.classList.contains(`${prefix}-zoom-reset`)) {
                    this.resetZoom();
                }
            }) as EventListener);
        }

        this.addListener(
            this.container,
            'wheel',
            ((e: WheelEvent) => this.handleWheel(e)) as EventListener,
            { passive: false }
        );

        // Desktop mouse drag: keep existing full pan behavior
        this.addListener(
            this.container,
            'mousedown',
            ((e: MouseEvent) => this.handleMouseDown(e)) as EventListener
        );
        this.addListener(
            document,
            'mousemove',
            ((e: MouseEvent) => this.handleMouseMove(e)) as EventListener
        );
        this.addListener(
            document,
            'mouseup',
            (() => this.handleMouseUp()) as EventListener
        );

        // iOS Safari / iOS WebKit fallback
        if (this.useTouchFallback) {
            this.addListener(
                this.container,
                'touchstart',
                ((e: TouchEvent) => this.handleTouchStart(e)) as EventListener,
                { passive: false }
            );
            // A second finger anywhere on the page ends a tap candidacy. The
            // container listeners never see a finger that starts and lifts outside
            // the container without moving, so watch the whole document for it.
            this.addListener(
                document,
                'touchstart',
                ((e: TouchEvent) => {
                    if (e.touches.length >= 2) this.tapCandidate = null;
                }) as EventListener,
                { capture: true, passive: true }
            );
            this.addListener(
                this.container,
                'touchmove',
                ((e: TouchEvent) => this.handleTouchMove(e)) as EventListener,
                { passive: false }
            );
            this.addListener(
                this.container,
                'touchend',
                ((e: TouchEvent) => this.handleTouchEnd(e)) as EventListener,
                { passive: false }
            );
            this.addListener(
                this.container,
                'touchcancel',
                ((e: TouchEvent) => this.handleTouchEnd(e)) as EventListener,
                { passive: false }
            );
            // Capture phase, so a duplicate native click is stopped before any
            // listener on the container or below sees it.
            this.addListener(
                this.container,
                'click',
                ((e: Event) => this.swallowDuplicateNativeClick(e)) as EventListener,
                { capture: true }
            );
        }

        // Pointer events for touch/pen (Android / non-iOS path)
        this.addListener(
            this.container,
            'pointerdown',
            ((e: PointerEvent) => this.handlePointerDown(e)) as EventListener,
            { passive: true }
        );
        this.addListener(
            this.container,
            'pointermove',
            ((e: PointerEvent) => this.handlePointerMove(e)) as EventListener,
            { passive: false }
        );
        this.addListener(
            this.container,
            'pointerup',
            ((e: PointerEvent) => this.handlePointerEnd(e)) as EventListener
        );
        this.addListener(
            this.container,
            'pointercancel',
            ((e: PointerEvent) => this.handlePointerEnd(e)) as EventListener
        );

        this.addListener(
            window,
            'resize',
            this.handleResize.bind(this) as EventListener
        );
    }

    private addListener(
        element: HTMLElement | Window | Document,
        event: string,
        handler: EventListener,
        options?: AddEventListenerOptions | boolean
    ): void {
        element.addEventListener(event, handler, options);
        this.listeners.push({ element, event, handler, options });
    }

    private getContainerSize(): { width: number; height: number } {
        const rect = this.container.getBoundingClientRect();
        return { width: rect.width, height: rect.height };
    }

    private getContentSize(): { width: number; height: number } {
        return {
            width: this.content.offsetWidth,
            height: this.content.offsetHeight,
        };
    }

    private getBoundaries() {
        return computePanBoundaries(
            this.getContainerSize(),
            this.getContentSize(),
            this.transform.scale
        );
    }

    /**
     * Stable Variant A:
     * - one-finger vertical => native page scroll
     * - one-finger horizontal => JS pan-x
     * - two-finger => JS pan-2d + pinch
     *
     * No edge-direction logic, no mid-gesture handoff.
     */
    private computeTouchAction(): string {
        if (this.useTouchFallback) {
            // On iOS:
            // - when not zoomed, let Safari scroll vertically
            // - when zoomed, own the gesture fully
            return this.isZoomed() ? 'none' : 'pan-y';
        }

        if (this.pointerMode === 'pan-x' || this.pointerMode === 'pan-2d') {
            return 'none';
        }

        return 'pan-y';
    }

    private getActivePointersArray(): ActivePointer[] {
        return Array.from(this.activePointers.values()).sort((a, b) => a.id - b.id);
    }

    private computeDistance(p1: ActivePointer, p2: ActivePointer): number {
        const dx = p2.clientX - p1.clientX;
        const dy = p2.clientY - p1.clientY;
        return Math.hypot(dx, dy);
    }

    private computeMidpoint(p1: ActivePointer, p2: ActivePointer): Point {
        const rect = this.container.getBoundingClientRect();
        return {
            x: (p1.clientX + p2.clientX) / 2 - rect.left,
            y: (p1.clientY + p2.clientY) / 2 - rect.top,
        };
    }

    private getTouchPoint(touch: Touch): Point {
        const rect = this.container.getBoundingClientRect();
        return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
        };
    }

    private computeTouchDistance(t1: Touch, t2: Touch): number {
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        return Math.hypot(dx, dy);
    }

    private computeTouchMidpoint(t1: Touch, t2: Touch): Point {
        const rect = this.container.getBoundingClientRect();
        return {
            x: (t1.clientX + t2.clientX) / 2 - rect.left,
            y: (t1.clientY + t2.clientY) / 2 - rect.top,
        };
    }

    private handleWheel(e: WheelEvent): void {
        const rect = this.container.getBoundingClientRect();
        const delta = -e.deltaY * this.config.scrollMultiplier;
        const newScale = Math.max(
            this.config.minZoom,
            Math.min(this.config.maxZoom, this.transform.scale + delta)
        );

        // The wheel belongs to the drawing only while it changes the zoom. At the
        // zoom limit it scrolls the page, so the sticky drawing cannot trap the
        // pointer while the customer scrolls the parts table.
        if (newScale !== this.transform.scale) {
            e.preventDefault();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            this.zoomToPoint(newScale, { x: mouseX, y: mouseY });
        }
    }

    private handleMouseDown(e: MouseEvent): void {
        if (e.button !== 0) return;
        // A press on a zoom button is a click, not the start of a drag; preventDefault()
        // below would also keep the button from taking focus.
        if (this.controlsEl?.contains(e.target as Node)) return;

        const rect = this.container.getBoundingClientRect();
        if (
            !canPan(
                { width: rect.width, height: rect.height },
                { width: this.content.offsetWidth, height: this.content.offsetHeight },
                this.transform.scale
            )
        ) {
            return;
        }

        this.isDragging = true;
        this.dragStart = { x: e.clientX, y: e.clientY };
        this.dragInitial = {
            x: this.transform.translateX,
            y: this.transform.translateY,
        };
        this.updateCursor();
        e.preventDefault();
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.isDragging) return;

        const dx = e.clientX - this.dragStart.x;
        const dy = e.clientY - this.dragStart.y;

        this.transform.translateX = this.dragInitial.x + dx;
        this.transform.translateY = this.dragInitial.y + dy;

        this.applyTransform();
    }

    private handleMouseUp(): void {
        if (this.isDragging) {
            this.isDragging = false;
            this.updateCursor();
        }
    }

    private handlePointerDown(e: PointerEvent): void {
        if (this.useTouchFallback) return;
        if (e.pointerType === 'mouse') return;

        this.activePointers.set(e.pointerId, {
            id: e.pointerId,
            clientX: e.clientX,
            clientY: e.clientY,
        });

        if (this.activePointers.size === 1) {
            this.pointerMode = 'pending';
            this.oneFingerStart = { x: e.clientX, y: e.clientY };
            this.oneFingerInitial = {
                x: this.transform.translateX,
                y: this.transform.translateY,
            };
            this.container.style.touchAction = this.computeTouchAction();
            return;
        }

        if (this.activePointers.size === 2) {
            const [p1, p2] = this.getActivePointersArray();
            this.pointerMode = 'pan-2d';
            this.lastPinchDistance = this.computeDistance(p1, p2);
            this.lastPinchMidpoint = this.computeMidpoint(p1, p2);
            this.container.style.touchAction = 'none';
        }
    }

    private handlePointerMove(e: PointerEvent): void {
        if (this.useTouchFallback) return;
        if (e.pointerType === 'mouse') return;
        if (!this.activePointers.has(e.pointerId)) return;

        this.activePointers.set(e.pointerId, {
            id: e.pointerId,
            clientX: e.clientX,
            clientY: e.clientY,
        });

        // Two-finger pinch + pan
        if (this.activePointers.size >= 2) {
            const [p1, p2] = this.getActivePointersArray();
            if (!p1 || !p2) return;

            if (this.pointerMode !== 'pan-2d') {
                this.pointerMode = 'pan-2d';
                this.lastPinchDistance = this.computeDistance(p1, p2);
                this.lastPinchMidpoint = this.computeMidpoint(p1, p2);
                this.container.style.touchAction = 'none';
                return;
            }

            if (this.lastPinchDistance === null || this.lastPinchMidpoint === null) {
                this.lastPinchDistance = this.computeDistance(p1, p2);
                this.lastPinchMidpoint = this.computeMidpoint(p1, p2);
                return;
            }

            e.preventDefault();

            const currentDistance = this.computeDistance(p1, p2);
            const currentMidpoint = this.computeMidpoint(p1, p2);

            // pinch zoom
            const delta = (currentDistance - this.lastPinchDistance) * this.config.pinchSensitivity;
            const prevScale = this.transform.scale;
            const newScale = Math.max(
                this.config.minZoom,
                Math.min(this.config.maxZoom, prevScale + delta)
            );

            if (newScale !== prevScale) {
                this.zoomToPoint(newScale, this.lastPinchMidpoint);
            }

            // two-finger pan x/y
            if (this.transform.scale > this.config.minZoom) {
                this.transform.translateX += currentMidpoint.x - this.lastPinchMidpoint.x;
                this.transform.translateY += currentMidpoint.y - this.lastPinchMidpoint.y;
                this.applyTransform();
            }

            this.lastPinchDistance = currentDistance;
            this.lastPinchMidpoint = currentMidpoint;
            return;
        }

        // One-finger gesture
        const dx = e.clientX - this.oneFingerStart.x;
        const dy = e.clientY - this.oneFingerStart.y;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const distance = Math.hypot(dx, dy);

        if (this.transform.scale <= this.config.minZoom) {
            this.pointerMode = 'scroll-native';
            this.container.style.touchAction = 'pan-y';
            return;
        }

        if (this.pointerMode === 'pending') {
            if (distance < this.pointerSlop) {
                return;
            }

            if (absDx > absDy) {
                // One finger horizontal pan only
                this.pointerMode = 'pan-x';
                this.container.style.touchAction = 'none';
            } else {
                // One finger vertical scroll only
                this.pointerMode = 'scroll-native';
                this.container.style.touchAction = 'pan-y';
                return;
            }
        }

        if (this.pointerMode === 'scroll-native') {
            return;
        }

        if (this.pointerMode === 'pan-x') {
            e.preventDefault();

            const boundaries = this.getBoundaries();
            const desiredX = this.oneFingerInitial.x + dx;
            const clampedX = Math.max(boundaries.minX, Math.min(boundaries.maxX, desiredX));

            this.transform.translateX = clampedX;
            // y unchanged in one-finger mode
            this.applyTransform();
        }
    }

    private handlePointerEnd(e: PointerEvent): void {
        if (this.useTouchFallback) return;
        if (e.pointerType === 'mouse') return;
        if (!this.activePointers.has(e.pointerId)) return;

        this.activePointers.delete(e.pointerId);

        if (this.activePointers.size === 1) {
            const [remaining] = this.getActivePointersArray();
            this.pointerMode = 'pending';
            this.oneFingerStart = { x: remaining.clientX, y: remaining.clientY };
            this.oneFingerInitial = {
                x: this.transform.translateX,
                y: this.transform.translateY,
            };
            this.lastPinchDistance = null;
            this.lastPinchMidpoint = null;
            this.container.style.touchAction = this.computeTouchAction();
            return;
        }

        if (this.activePointers.size === 0) {
            this.pointerMode = 'idle';
            this.lastPinchDistance = null;
            this.lastPinchMidpoint = null;
            this.container.style.touchAction = this.computeTouchAction();
        }
    }

    private handleTouchStart(e: TouchEvent): void {
        if (!this.useTouchFallback) return;

        if (e.touches.length >= 2) {
            const t1 = e.touches[0];
            const t2 = e.touches[1];

            this.touchPassesThrough = false;
            this.tapCandidate = null;
            this.pointerMode = 'pan-2d';
            this.lastTouchDistance = this.computeTouchDistance(t1, t2);
            this.lastTouchMidpoint = this.computeTouchMidpoint(t1, t2);
            this.lastTouchPoint = null;
            this.touchStartPoint = null;
            this.iosOwnsGesture = true;

            this.container.style.touchAction = 'none';
            e.preventDefault();
            return;
        }

        if (e.touches.length === 1) {
            this.tapCandidate = null;
            // Taps on the zoom buttons and on passthrough elements (the zoom hint's
            // "Verstanden!") must fire as clicks — don't intercept them.
            // e.preventDefault() on touchstart suppresses the synthetic click on iOS.
            const prefix = this.config.controlsPrefix;
            if ((e.target as HTMLElement).closest(`.${prefix}-zoom-controls, [${ZOOM_PASSTHROUGH_ATTR}]`)) {
                this.touchPassesThrough = true;
                return;
            }
            this.touchPassesThrough = false;

            const point = this.getTouchPointFromList(e.touches, 0);
            this.touchStartPoint = point;
            this.lastTouchPoint = point;
            this.lastTouchClientY = e.touches[0].clientY;
            this.lastTouchDistance = null;
            this.lastTouchMidpoint = null;

            if (this.isZoomed()) {
                this.pointerMode = 'pending';
                this.iosOwnsGesture = true;
                this.container.style.touchAction = 'none';
                e.preventDefault();
                // The click this preventDefault() suppresses is delivered by us if
                // the touch turns out to be a tap. The touchstart target is what the
                // finger landed on; nothing moves while the touch stays 'pending'.
                this.tapCandidate = { target: e.target, startedAt: performance.now() };
            } else {
                this.pointerMode = 'scroll-native';
                this.iosOwnsGesture = false;
                this.targetScrollY = window.scrollY;
                this.container.style.touchAction = 'pan-y';
            }
        }
    }

    private handleTouchMove(e: TouchEvent): void {
        if (!this.useTouchFallback) return;
        if (this.touchPassesThrough && e.touches.length === 1) return;

        // Two-finger pinch + pan
        if (e.touches.length >= 2) {
            // `touches` is document-wide: a second finger that landed OUTSIDE the
            // container never fired our touchstart, so re-arm the pinch here too.
            this.touchPassesThrough = false;
            this.tapCandidate = null;
            const t1 = e.touches[0];
            const t2 = e.touches[1];

            const currentDistance = this.computeTouchDistance(t1, t2);
            const currentMidpoint = this.computeTouchMidpoint(t1, t2);

            if (this.pointerMode !== 'pan-2d') {
                this.pointerMode = 'pan-2d';
                this.lastTouchDistance = currentDistance;
                this.lastTouchMidpoint = currentMidpoint;
                this.lastTouchPoint = null;
                this.touchStartPoint = null;
            }

            if (this.lastTouchDistance === null || this.lastTouchMidpoint === null) {
                this.lastTouchDistance = currentDistance;
                this.lastTouchMidpoint = currentMidpoint;
                this.container.style.touchAction = 'none';
                e.preventDefault();
                return;
            }

            this.container.style.touchAction = 'none';
            e.preventDefault();

            const delta = (currentDistance - this.lastTouchDistance) * this.config.pinchSensitivity;
            const prevScale = this.transform.scale;
            const newScale = Math.max(
                this.config.minZoom,
                Math.min(this.config.maxZoom, prevScale + delta)
            );

            if (newScale !== prevScale) {
                this.zoomToPoint(newScale, this.lastTouchMidpoint);
            }

            if (this.transform.scale > this.config.minZoom) {
                this.transform.translateX += currentMidpoint.x - this.lastTouchMidpoint.x;
                this.transform.translateY += currentMidpoint.y - this.lastTouchMidpoint.y;
                this.applyTransform();
            }

            this.lastTouchDistance = currentDistance;
            this.lastTouchMidpoint = currentMidpoint;
            this.iosOwnsGesture = true;
            return;
        }

        // One-finger path
        if (e.touches.length === 1) {
            const point = this.getTouchPointFromList(e.touches, 0);
            const rawClientY = e.touches[0].clientY;

            if (!this.isZoomed()) {
                this.pointerMode = 'scroll-native';
                this.iosOwnsGesture = false;
                this.targetScrollY = window.scrollY;
                this.lastTouchPoint = point;
                this.lastTouchClientY = rawClientY;
                this.touchStartPoint = point;
                this.container.style.touchAction = 'pan-y';
                return;
            }

            if (!this.touchStartPoint) {
                this.touchStartPoint = point;
            }
            if (!this.lastTouchPoint) {
                this.lastTouchPoint = point;
                this.lastTouchClientY = rawClientY;
            }

            const totalDx = point.x - this.touchStartPoint.x;
            const totalDy = point.y - this.touchStartPoint.y;
            const totalDistance = Math.hypot(totalDx, totalDy);

            if (this.pointerMode === 'pending') {
                if (totalDistance < this.touchSlop) {
                    this.container.style.touchAction = 'none';
                    e.preventDefault();
                    return;
                }
                // Moved past the slop: this is a pan or a page scroll, not a tap.
                this.tapCandidate = null;

                if (Math.abs(totalDy) > Math.abs(totalDx) && this.shouldAllowNativePageScroll(totalDy)) {
                    this.pointerMode = 'scroll-native';
                    this.iosOwnsGesture = false;
                    this.targetScrollY = window.scrollY;
                    this.lastTouchPoint = point;
                    this.lastTouchClientY = rawClientY;
                    this.touchStartPoint = point;
                    this.container.style.touchAction = 'pan-y';
                    return;
                }

                this.pointerMode = 'pan-2d';
            }

            const dxStep = point.x - this.lastTouchPoint.x;
            const dyStep = point.y - this.lastTouchPoint.y;

            if (this.pointerMode === 'scroll-native') {
                const delta = rawClientY - this.lastTouchClientY;
                this.doPageScroll(delta);
                this.iosOwnsGesture = false;
                this.lastTouchPoint = point;
                this.lastTouchClientY = rawClientY;
                this.container.style.touchAction = 'pan-y';
                return;
            }

            if (this.shouldAllowNativePageScroll(dyStep)) {
                this.pointerMode = 'scroll-native';
                this.iosOwnsGesture = false;
                this.targetScrollY = window.scrollY;
                this.doPageScroll(rawClientY - this.lastTouchClientY);
                this.lastTouchPoint = point;
                this.lastTouchClientY = rawClientY;
                this.touchStartPoint = point;
                this.container.style.touchAction = 'pan-y';
                return;
            }

            this.pointerMode = 'pan-2d';
            this.container.style.touchAction = 'none';
            e.preventDefault();

            this.applyIosOneFingerPan(dxStep, dyStep);

            this.lastTouchPoint = point;
            this.lastTouchClientY = rawClientY;
            this.iosOwnsGesture = true;
            return;
        }
    }

    private handleTouchEnd(e: TouchEvent): void {
        if (!this.useTouchFallback) return;
        // Taken now so a second finger lifting first, or a cancel, ends the candidacy
        // for good; it is only delivered below once every finger is up.
        const tapCandidate = this.tapCandidate;
        this.tapCandidate = null;

        if (this.touchPassesThrough) {
            if (e.touches.length > 0) return;
            // Last finger lifted: fall through to the idle reset below.
            this.touchPassesThrough = false;
        }

        if (e.touches.length >= 2) {
            const t1 = e.touches[0];
            const t2 = e.touches[1];

            this.pointerMode = 'pan-2d';
            this.lastTouchDistance = this.computeTouchDistance(t1, t2);
            this.lastTouchMidpoint = this.computeTouchMidpoint(t1, t2);
            this.lastTouchPoint = null;
            this.touchStartPoint = null;
            this.iosOwnsGesture = true;

            this.container.style.touchAction = 'none';
            return;
        }

        if (e.touches.length === 1) {
            const point = this.getTouchPointFromList(e.touches, 0);

            this.touchStartPoint = point;
            this.lastTouchPoint = point;
            this.lastTouchClientY = e.touches[0].clientY;
            this.lastTouchDistance = null;
            this.lastTouchMidpoint = null;

            if (this.isZoomed()) {
                // Stay in zoomed one-finger custom pan mode
                this.pointerMode = 'pending';
                this.iosOwnsGesture = true;
                this.container.style.touchAction = 'none';
            } else {
                this.pointerMode = 'scroll-native';
                this.iosOwnsGesture = false;
                this.targetScrollY = window.scrollY;
                this.container.style.touchAction = 'pan-y';
            }

            return;
        }

        this.pointerMode = 'idle';
        this.lastTouchDistance = null;
        this.lastTouchMidpoint = null;
        this.lastTouchPoint = null;
        this.touchStartPoint = null;
        this.iosOwnsGesture = false;
        this.container.style.touchAction = this.computeTouchAction();

        // Delivered after the reset above, so click handlers see an idle controller.
        if (
            tapCandidate &&
            e.type === 'touchend' &&
            performance.now() - tapCandidate.startedAt <= this.tapMaxDurationMs
        ) {
            this.deliverTapClick(tapCandidate.target, e);
        }
    }

    /**
     * Fire the click that preventDefault() on touchstart kept iOS from firing.
     * Targets the touchstart element rather than elementFromPoint() at the end:
     * a 'pending' touch never transformed the content, so the element under the
     * finger is unchanged — and the start target is what the user aimed at,
     * independent of layout. Dispatched for any target (a tap on a plain drawing
     * area clicks nothing, exactly as the native click does at zoom 1).
     */
    private deliverTapClick(target: EventTarget | null, e: TouchEvent): void {
        if (!target) return;
        const touch = e.changedTouches[0];
        const click = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch?.clientX ?? 0,
            clientY: touch?.clientY ?? 0,
        });
        this.lastSyntheticClick = click;
        this.nativeClickSuppression = {
            target,
            until: performance.now() + this.nativeClickSuppressionMs,
        };
        target.dispatchEvent(click);
    }

    private swallowDuplicateNativeClick(e: Event): void {
        const suppression = this.nativeClickSuppression;
        if (!suppression || e === this.lastSyntheticClick) return;
        if (performance.now() > suppression.until) {
            this.nativeClickSuppression = null;
            return;
        }
        if (e.target !== suppression.target) return;
        this.nativeClickSuppression = null;
        e.preventDefault();
        e.stopImmediatePropagation();
    }
    private handleResize(): void {
        const currentWidth = window.innerWidth;

        if (currentWidth !== this.previousWindowWidth) {
            this.previousWindowWidth = currentWidth;
            this.resetZoom();
        }
    }

    private isZoomed(): boolean {
        return this.transform.scale > this.config.minZoom;
    }

    private getTouchPointFromList(touches: TouchList, index: number): Point {
        const touch = touches[index];
        return this.getTouchPoint(touch);
    }

    private clampX(value: number): number {
        const boundaries = this.getBoundaries();
        return Math.max(boundaries.minX, Math.min(boundaries.maxX, value));
    }

    private clampY(value: number): number {
        const boundaries = this.getBoundaries();
        return Math.max(boundaries.minY, Math.min(boundaries.maxY, value));
    }

    private doPageScroll(dyStep: number): void {
        this.targetScrollY -= dyStep;
        window.scrollTo(0, this.targetScrollY);
    }

    private shouldAllowNativePageScroll(dy: number): boolean {
        if (!this.isZoomed()) {
            return true;
        }

        const boundaries = this.getBoundaries();
        const y = this.transform.translateY;
        const edgeTolerance = 40;

        const atTop = y >= boundaries.maxY - edgeTolerance;
        const atBottom = y <= boundaries.minY + edgeTolerance;
        const draggingDown = dy > 0;
        const draggingUp = dy < 0;

        return (atTop && draggingDown) || (atBottom && draggingUp);
    }

    private applyIosOneFingerPan(dxStep: number, dyStep: number): void {
        const nextX = this.clampX(this.transform.translateX + dxStep);
        const nextY = this.clampY(this.transform.translateY + dyStep);

        this.transform.translateX = nextX;
        this.transform.translateY = nextY;
        this.applyTransform();
    }

    private zoomToPoint(newScale: number, point: Point): void {
        const scaleDiff = newScale / this.transform.scale;

        this.transform.translateX = point.x - scaleDiff * (point.x - this.transform.translateX);
        this.transform.translateY = point.y - scaleDiff * (point.y - this.transform.translateY);
        this.transform.scale = newScale;

        this.applyTransform();
    }

    private applyTransform(): void {
        const rect = this.container.getBoundingClientRect();

        this.transform = clampTransform(
            this.transform,
            { width: rect.width, height: rect.height },
            { width: this.content.offsetWidth, height: this.content.offsetHeight }
        );

        this.container.style.touchAction = this.computeTouchAction();

        this.content.style.transform = `translate3d(${this.transform.translateX}px, ${this.transform.translateY}px, 0) scale(${this.transform.scale})`;

        this.updateCursor();
        this.config.onTransformChange(this.transform);
    }

    private updateCursor(): void {
        const rect = this.container.getBoundingClientRect();
        const isPannable = canPan(
            { width: rect.width, height: rect.height },
            { width: this.content.offsetWidth, height: this.content.offsetHeight },
            this.transform.scale
        );

        const prefix = this.config.controlsPrefix;
        this.container.classList.remove(`${prefix}-grabbable`, `${prefix}-grabbing`);

        if (isPannable) {
            this.container.classList.add(
                this.isDragging ? `${prefix}-grabbing` : `${prefix}-grabbable`
            );
        }
    }

    public zoomIn(): void {
        const newScale = Math.min(this.config.maxZoom, this.transform.scale + this.config.zoomStep);
        if (newScale !== this.transform.scale) {
            const rect = this.container.getBoundingClientRect();
            this.zoomToPoint(newScale, { x: rect.width / 2, y: rect.height / 2 });
        }
    }

    public zoomOut(): void {
        const newScale = Math.max(this.config.minZoom, this.transform.scale - this.config.zoomStep);
        if (newScale !== this.transform.scale) {
            const rect = this.container.getBoundingClientRect();
            this.zoomToPoint(newScale, { x: rect.width / 2, y: rect.height / 2 });
        }
    }

    public resetZoom(): void {
        this.transform = {
            scale: 1,
            translateX: 0,
            translateY: 0,
        };
        this.applyTransform();
    }

    public getTransform(): Transform {
        return { ...this.transform };
    }

    public setTransform(transform: Partial<Transform>): void {
        this.transform = {
            scale: transform.scale ?? this.transform.scale,
            translateX: transform.translateX ?? this.transform.translateX,
            translateY: transform.translateY ?? this.transform.translateY,
        };
        this.applyTransform();
    }

    private setupDiscoveryAnimation(): void {
        // One-time mouseenter trigger (desktop hover)
        const hoverHandler = () => {
            this.triggerDiscoveryAnimation();
            this.container.removeEventListener('mouseenter', hoverHandler);
        };
        this.container.addEventListener('mouseenter', hoverHandler);

        // IntersectionObserver trigger: fire when ≥80% of the image scrolls into view
        if ('IntersectionObserver' in window) {
            const image = this.content.querySelector('img');
            const observe = (target: Element) => {
                this.discoveryObserver = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting && entry.intersectionRatio >= 0.8) {
                                this.triggerDiscoveryAnimation();
                            }
                        });
                    },
                    { threshold: [0.8] }
                );
                this.discoveryObserver.observe(target);
            };

            if (image) {
                if (image.complete && image.naturalWidth > 0) {
                    observe(image);
                } else {
                    image.addEventListener('load', () => observe(image), { once: true });
                }
            }
        }
    }

    private triggerDiscoveryAnimation(): void {
        if (this.hasShownDiscovery) return;
        this.hasShownDiscovery = true;

        if (this.discoveryObserver) {
            this.discoveryObserver.disconnect();
            this.discoveryObserver = null;
        }

        // Respect reduced-motion preference
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const prefix = this.config.controlsPrefix;
        const controls = this.controlsEl;
        const zoomButtons = Array.from(
            controls?.querySelectorAll<HTMLElement>(`.${prefix}-zoom-button`) ?? []
        );

        // --- Phase 1: scale-pulse the diagram content (0 – 700ms) ---
        const { scale, translateX, translateY } = this.transform;
        const centerX = this.content.offsetWidth / 2;
        const centerY = this.content.offsetHeight / 2;
        const pulseScale = scale * 1.05;
        const ratio = pulseScale / scale;

        this.content.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        this.content.style.transform =
            `translate3d(${translateX - centerX * (ratio - 1)}px, ${translateY - centerY * (ratio - 1)}px, 0) scale(${pulseScale})`;

        window.setTimeout(() => {
            // A zoom/pan during the pulse already wrote the current transform; the
            // snapshot is stale then and must not overwrite it.
            const t = this.transform;
            if (t.scale === scale && t.translateX === translateX && t.translateY === translateY) {
                this.content.style.transform =
                    `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
            }
        }, 400);

        window.setTimeout(() => {
            this.content.style.transition = '';
        }, 700);

        // --- Phase 2: highlight zoom controls (700 – 2700ms) ---
        window.setTimeout(() => {
            if (controls) {
                zoomButtons.forEach((btn) => btn.classList.add(`${prefix}-zoom-buttons-highlighted`));

                controls.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
                controls.style.transform = 'scale(1.05)';

                window.setTimeout(() => {
                    controls.style.transform = '';
                }, 400);

                window.setTimeout(() => {
                    controls.style.transition = '';
                }, 700);
            }
        }, 700);

        window.setTimeout(() => {
            zoomButtons.forEach((btn) => btn.classList.remove(`${prefix}-zoom-buttons-highlighted`));
        }, 2700);
    }

    public destroy(): void {
        if (this.discoveryObserver) {
            this.discoveryObserver.disconnect();
            this.discoveryObserver = null;
        }

        this.listeners.forEach(({ element, event, handler, options }) => {
            element.removeEventListener(event, handler, options);
        });
        this.listeners = [];

        const prefix = this.config.controlsPrefix;
        this.controlsEl?.remove();
        this.controlsEl = null;

        this.content.style.transform = '';
        this.container.classList.remove(`${prefix}-grabbable`, `${prefix}-grabbing`);
    }
}